import React, { useState } from "react";
import {
  ArrowLeft,
  Sun,
  Moon,
  Smartphone,
  Mail,
  Settings,
  Trash2,
  AlertTriangle,
  ChevronRight,
  User,
  Clock,
} from "lucide-react";

const DeleteAccount = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const deletionSteps = [
    {
      step: "1",
      title: "Open Homeegram App",
      description: "Launch the Homeegram mobile application on your device",
      icon: <Smartphone className="w-6 h-6" />,
    },
    {
      step: "2",
      title: "Go to Settings",
      description: "Navigate to Settings from your profile or main menu",
      icon: <Settings className="w-6 h-6" />,
    },
    {
      step: "3",
      title: "Find Account Settings",
      description: "Look for 'Account' or 'Account Settings' option",
      icon: <User className="w-6 h-6" />,
    },
    {
      step: "4",
      title: "Delete Account",
      description: "Tap on 'Delete Account' & follow the confirmation steps",
      icon: <Trash2 className="w-6 h-6" />,
    },
  ];

  const deletionInfo = [
    "Your profile & personal information will be permanently removed",
    "All project portfolio & work history will be deleted",
    "Professional connections & messages will be lost",
    "Verification status & credentials will be removed",
    "Subscription & payment history will be deleted",
    "This action cannot be undone after completion",
  ];

  return (
    <div
      className={`min-h-screen transition-colors duration-300 font-['Poppins'] ${
        isDarkMode ? "bg-black text-white" : "bg-white text-gray-900"
      }`}
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 border-b ${
          isDarkMode
            ? "bg-black/95 backdrop-blur-md border-gray-800"
            : "bg-white/95 backdrop-blur-md border-gray-200"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.history.back()}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"
                }`}
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center space-x-3">
                <Trash2 className="w-8 h-8 text-red-500" />
                <h1 className="text-xl font-semibold">Delete Account</h1>
              </div>
            </div>

            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-colors ${
                isDarkMode
                  ? "bg-gray-800 text-[#FEF200] hover:bg-gray-700"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-semibold mb-6">
            How to Delete Your Account
          </h1>
          <p
            className={`text-lg leading-relaxed max-w-3xl mx-auto ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            You can delete your Homeegram account directly from the mobile app.
            Follow the steps below or contact our grievance team for assistance.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Side - In-App Deletion Steps */}
          <div>
            <div
              className={`rounded-2xl p-8 shadow-lg ${
                isDarkMode
                  ? "bg-gray-800 border border-gray-700"
                  : "bg-white border border-gray-200"
              }`}
            >
              <h2 className="text-2xl font-semibold mb-6 flex items-center">
                <Smartphone className="w-8 h-8 mr-3 text-[#FEF200]" />
                Delete Account In-App
              </h2>

              <div className="space-y-6">
                {deletionSteps.map((step, index) => (
                  <div key={index} className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-[#FEF200] text-black rounded-full flex items-center justify-center font-semibold text-lg">
                        {step.step}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold mb-2 group-hover:text-[#FEF200] transition-colors">
                        {step.title}
                      </h3>
                      <p
                        className={`text-sm leading-relaxed ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {step.description}
                      </p>
                    </div>
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        isDarkMode ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      {step.icon}
                    </div>
                  </div>
                ))}
              </div>

              <div
                className={`mt-8 p-4 rounded-lg ${
                  isDarkMode ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                <p className="text-sm text-center">
                  <strong>Path:</strong> Settings → Account → Delete Account
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Alternative Contact & Info */}
          <div className="space-y-8">
            {/* Contact Support */}
            <div
              className={`rounded-2xl p-8 shadow-lg ${
                isDarkMode
                  ? "bg-gray-800 border border-gray-700"
                  : "bg-white border border-gray-200"
              }`}
            >
              <h2 className="text-2xl font-semibold mb-6 flex items-center">
                <Mail className="w-8 h-8 mr-3 text-[#FEF200]" />
                Need Help?
              </h2>

              <p
                className={`text-sm mb-6 ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                If you're unable to delete your account through the app or need
                assistance, contact our grievance team:
              </p>

              <div
                className={`p-6 rounded-lg border-2 border-yellow-400 ${
                  isDarkMode ? "bg-gray-700" : "bg-yellow-50"
                }`}
              >
                <div className="text-center">
                  <Mail className="w-8 h-8 text-[#FEF200] mx-auto mb-3" />
                  <h3 className="text-xl font-semibold mb-2">Grievance Contact</h3>
                  <a
                    href="mailto:grievance@homeegram.com"
                    className="text-[#FEF200] hover:text-yellow-500 font-medium text-lg transition-colors cursor-pointer"
                  >
                    grievance@homeegram.com
                  </a>
                  <p
                    className={`text-sm mt-3 ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Include your registered email & reason for deletion
                  </p>
                </div>
              </div>

              <div
                className={`mt-6 p-4 rounded-lg ${
                  isDarkMode ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-[#FEF200]" />
                  <span className="text-sm font-medium">
                    Response Time: 24-48 hours
                  </span>
                </div>
              </div>
            </div>

            {/* What Happens During Deletion */}
            <div
              className={`rounded-2xl p-8 shadow-lg ${
                isDarkMode
                  ? "bg-gray-800 border border-gray-700"
                  : "bg-white border border-gray-200"
              }`}
            >
              <h2 className="text-2xl font-semibold mb-6 flex items-center">
                <AlertTriangle className="w-8 h-8 mr-3 text-red-500" />
                What Gets Deleted
              </h2>

              <div className="space-y-3">
                {deletionInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      {info}
                    </p>
                  </div>
                ))}
              </div>

              <div
                className={`mt-6 p-4 rounded-lg border-l-4 border-red-500 ${
                  isDarkMode ? "bg-red-900/20" : "bg-red-50"
                }`}
              >
                <p className="text-sm text-red-600 font-medium">
                  ⚠️ This action is permanent & cannot be undone. Please
                  consider downloading your data before proceeding.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Download App CTA */}
        <div
          className={`mt-16 p-8 rounded-2xl text-center ${
            isDarkMode
              ? "bg-gradient-to-r from-gray-800 to-gray-700"
              : "bg-gradient-to-r from-gray-50 to-gray-100"
          }`}
        >
          <Smartphone className="w-12 h-12 text-[#FEF200] mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-4">Don't Have the App?</h3>
          <p
            className={`mb-6 max-w-2xl mx-auto ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Download the Homeegram mobile app to manage your account settings,
            including account deletion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-[#FEF200] hover:bg-yellow-500 text-black font-semibold py-3 px-8 rounded-full transition-colors">
              Download App
            </button>
            <a
              href="mailto:grievance@homeegram.com"
              className={`border-2 font-semibold py-3 px-8 rounded-full transition-colors ${
                isDarkMode
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Contact Grievance Team
            </a>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-12 text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/privacy-policy"
              className={`inline-flex items-center px-6 py-3 rounded-full font-medium transition-colors ${
                isDarkMode
                  ? "bg-gray-800 hover:bg-gray-700 text-white"
                  : "bg-white hover:bg-gray-50 text-gray-700 border border-gray-200"
              }`}
            >
              Privacy Policy
              <ChevronRight className="w-4 h-4 ml-2" />
            </a>
            <a
              href="/terms-and-conditions"
              className={`inline-flex items-center px-6 py-3 rounded-full font-medium transition-colors ${
                isDarkMode
                  ? "bg-gray-800 hover:bg-gray-700 text-white"
                  : "bg-white hover:bg-gray-50 text-gray-700 border border-gray-200"
              }`}
            >
              Terms & Conditions
              <ChevronRight className="w-4 h-4 ml-2" />
            </a>
          </div>
        </div>
      </main>

      <style jsx>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
};

export default DeleteAccount;
